import type { Seal_Motor_Oil, Seals } from "gql/api";
import type { Maybe } from "graphql/jsutils/Maybe";

export function getSealAdjustedBearingRatingAndViscosityCp(
  bearing_rating: number,
  mf_test_temp1: number,
  mf_test_temp2: number,
  mf_test_visc1: number,
  mf_test_visc2: number,
  mf_spg: number,
  op_temp: number
): { bearingRating: number; viscosityCp: number } {
  function log10(val: number): number {
    return Math.log(val) / Math.LN10;
  }

  let bearing_rating_3500;
  let cp = 1;
  if (op_temp <= 150 || mf_test_temp1 == null) {
    bearing_rating_3500 = bearing_rating;
  } else {
    const op_temp_c = (op_temp - 32) / 1.8;
    const t1_r = mf_test_temp1 + 273;
    const t2_r = mf_test_temp2 + 273;
    const temp_r = op_temp_c + 273;

    const lt1 = log10(t1_r);
    const lt2 = log10(t2_r);
    const llc_st1 = log10(log10(mf_test_visc1 + 0.7));
    const llc_st2 = log10(log10(mf_test_visc2 + 0.7));
    const b = (llc_st2 - llc_st1) / (lt1 - lt2);
    const a = llc_st1 + b * lt1;
    const lt3 = log10(temp_r);
    const y = Math.pow(10, a - b * lt3);
    const y2 = Math.pow(10, y);
    const cst = y2 - 0.7;
    // viscosity of bearing oil
    cp = cst / mf_spg;
    bearing_rating_3500 = bearing_rating * (0.68638 + 0.044803 * cp);
    if (bearing_rating_3500 > bearing_rating) {
      bearing_rating_3500 = bearing_rating;
    }
  }

  return { bearingRating: bearing_rating_3500, viscosityCp: cp };
}

export type SealData = {
  bearingRatingAt60hzLBS: number;
  bearingRatingAtRPMLBS: number;
  viscosityCp: number;
};

export function getSealBearingData(
  seal: Maybe<Partial<Seals>>,
  operatingRPM: number,
  motorSkinTemperatureF: number,
  sealMotorOil: Seal_Motor_Oil | null | undefined
): SealData {
  const result = {
    bearingRatingAt60hzLBS: 0,
    bearingRatingAtRPMLBS: 0,
    viscosityCp: 0
  };

  if (
    seal &&
    seal.standard_bearing_rating_pounds &&
    sealMotorOil?.mf_test_temp1_celsius &&
    sealMotorOil?.mf_test_temp2_celsius &&
    sealMotorOil?.viscosity_test1_centistroke &&
    sealMotorOil?.viscosity_test2_centistroke &&
    sealMotorOil?.specific_gravity
  ) {
    const bearingRatingData = getSealAdjustedBearingRatingAndViscosityCp(
      seal.standard_bearing_rating_pounds,
      sealMotorOil.mf_test_temp1_celsius,
      sealMotorOil.mf_test_temp2_celsius,
      sealMotorOil.viscosity_test1_centistroke,
      sealMotorOil.viscosity_test2_centistroke,
      sealMotorOil.specific_gravity,
      motorSkinTemperatureF
    );

    result.bearingRatingAt60hzLBS = bearingRatingData.bearingRating;
    result.bearingRatingAtRPMLBS =
      bearingRatingData.bearingRating * (operatingRPM / 3500);
    result.viscosityCp = bearingRatingData.viscosityCp;
  }

  return result;
}

export function getShutInSealThrustData(
  delta_housing_pressure: number,
  pump_shaft_diameter: number
): number {
  const shaft_radius = pump_shaft_diameter / 2;

  const thrust_load_lbs =
    Math.PI * shaft_radius * shaft_radius * delta_housing_pressure;

  return thrust_load_lbs;
}
